<template>
  <div>
    <v-data-table
      class="table-widget insights-report-table"
      dense
      data-private
      :headers="headers"
      :items="items"
      :footer-props="footerOptions"
      :hide-default-footer="items.length <= itemsPerPage"
    >
      <template v-slot:item="{ item }">
        <tr>
          <template v-for="(type, index) in headerTypes">
            <td :key="type + index" :class="getClasses(type)">
              <div v-if="type.includes('trend')">
                <template v-if="item[type] !== 0">
                  <v-icon
                    v-if="item[type] > 0"
                    class="up"
                    data-testid="table-trend-icon"
                  >
                    mdi-chevron-up
                  </v-icon>

                  <v-icon v-else class="down" data-testid="table-trend-icon">
                    mdi-chevron-down
                  </v-icon>
                </template>
                <v-icon v-else data-testid="table-trend-icon">remove</v-icon>
              </div>

              <div
                v-else-if="type === 'predictedFinancialImpactInUSD'"
                data-testid="table-cell-predictedFinancialImpactInUSD"
              >
                {{ formatCurrency(item[type]) }}
              </div>

              <div v-else-if="isObject(item[type])">
                <v-hover v-slot:default="{ hover }">
                  <a
                    v-html="sanitizeContent(item[type] ? item[type].text : '')"
                    data-testid="table-link-item"
                    :class="{ 'text-decoration-underline': hover }"
                    :href="
                      getEntityLink(item[type] ? item[type].entityData : {})
                    "
                  ></a>
                </v-hover>
              </div>

              <div
                v-else
                data-testid="table-html-content"
                v-html="sanitizeContent(item[type])"
              ></div>
            </td>
          </template>
        </tr>
      </template>

      <template v-slot:body.append v-if="footers.length">
        <tr v-for="(footer, index) in footers" :key="index" :class="footer">
          <template v-for="(item, key) in footer">
            <td :key="item + key" :class="getClasses(key)">
              <div
                data-testid="table-html-content"
                v-html="sanitizeContent(item)"
              ></div>
            </td>
          </template>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

export default {
  name: 'Table',

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      defaultItemsPerPage: 10,
    }
  },

  computed: {
    ...mapGetters(['organizationId']),
    itemsPerPage() {
      return this.data?.itemsPerPage || this.defaultItemsPerPage
    },

    headers() {
      const headers = this.data?.headers ?? []

      return (
        headers.map(header => ({
          ...header,
          text:
            header.value === 'predictedFinancialImpactInUSD'
              ? this.$t('insights.predicted_financial_impact.impact_in_usd')
              : header.text,
          class: header.nowrap && 'text-no-wrap',
        })) ?? []
      )
    },

    headerTypes() {
      return this.headers.map(h => h.value)
    },

    headersByValue() {
      return _.keyBy(this.headers, 'value')
    },

    items() {
      return this.data?.items ?? []
    },

    footers() {
      return this.data?.footers ?? []
    },

    footerOptions() {
      return { itemsPerPageOptions: [this.itemsPerPage] || [] }
    },
  },

  methods: {
    sanitizeContent(content) {
      return this.$sanitize(content)
    },

    getClasses(type) {
      const headerAlingStyle = this.headersByValue[type].align
      const nowrapClass = this.headersByValue[type].nowrap ? 'text-no-wrap' : ''
      return [`text-${headerAlingStyle}`, nowrapClass]
    },

    getEntityLink(entityData) {
      if (entityData?.url) return `/${entityData.url}`

      if (!entityData?.entityId) {
        return `/${this.entityType}`
      }

      let kebabCaseType = _.kebabCase(entityData?.entityType)
      if (kebabCaseType === 'improvement-idea') kebabCaseType = 'rev'
      return `/${kebabCaseType}/${this.organizationId}/${entityData?.entityId}`
    },

    isObject(value) {
      return _.isObject(value)
    },

    formatCurrency(value) {
      if (!_.isNumber(value)) return '—'

      // Reports always display predicted impact in USD per product rule.
      return `$${Number(value).toLocaleString(navigator.language)}`
    },
  },
}
</script>

<style lang="scss" scoped>
.table-widget {
  border: solid 1px $color-blue-grey-lighten2;
}

.up {
  color: $color-green;
  font-size: 24px;
}

.down {
  color: $color-red;
  margin-top: -5px;
  font-size: 24px;
}
</style>

<style lang="scss">
.insights-report-table {
  td {
    &:not(:last-child) {
      border-right: 1px solid $color-blue-grey-lighten2;
    }
  }

  th {
    span {
      color: $color-blue-grey-darken3 !important;
    }

    &:not(:last-child) {
      border-right: 1px solid $color-blue-grey-lighten2 !important;
    }
  }
}
</style>
